"use client";

import { useDeferredValue, useMemo, useState } from "react";
import { Notice, TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CheckerLabels,
  CheckerReport,
} from "@/components/tools/reference-checkers/checker-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  checkIeeeReferences,
  IEEE_SOURCES,
  type IeeeReport,
  type IeeeRule,
} from "@/lib/citation-check-ieee";
import { t, tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_CHECKER_IEEE_STRINGS = {
  inputLabel: "IEEE reference list",
  inputPlaceholder:
    "[1] J. K. Author, “Name of paper,” Abbrev. Title of Periodical, vol. x, no. x, pp. xxx–xxx, Abbrev. Month, year, doi: xxx.",
  inputHint:
    "One reference per line, or wrapped lines under their [n] number. The check runs in your browser — nothing is uploaded.",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    '[1] M. M. Chiampi and L. L. Zilberti, “Induction of electric field in human bodies moving near MRI: An efficient BEM computational procedure,” IEEE Trans. Biomed. Eng., vol. 58, no. 10, pp. 2787–2793, Oct. 2011, doi: 10.1109/TBME.2011.2158315.\n[2] Zhang, J. and Tansu, N. "Optical gain and laser characteristics of InGaN quantum wells on ternary InGaN substrates", IEEE Photon. J., Vol. 5, No. 2, April 2013, Art. no. 2600111\n[4] R. Fardel, M. Nagel, F. Nuesch, T. Lippert and A. Wokaun, “Fabrication of organic light emitting diode pixels by laser-assisted forward transfer,” Appl. Phys. Lett., vol. 91, no. 6, Aug. 2007, Art. no. 061103.\n[3] F. Vatta, A. Soranzo, and F. Babich, “More accurate analysis of sum-product decoding of LDPC codes using a Gaussian approximation,” IEEE Commun. Lett., early access, Dec. 11, 2018, DOI: 10.1109/LCOMM.2018.2886261.',
  emptyPrompt: "Paste an IEEE reference list to check it.",
  results: "Results",
  summary: "{issues} across {entries}",
  issuesCount:
    "one:{count} likely issue to review|other:{count} likely issues to review",
  entriesCount: "one:{count} reference|other:{count} references",
  noIssues: "No likely issues found in {entries}.",
  noIssuesBody:
    "This is a first pass, not a guarantee. The checker reads the punctuation, order and abbreviations of each reference; it cannot tell whether a source exists, whether its details are right, or whether a journal abbreviation is the official one.",
  cleanCount:
    "one:{count} reference had no likely issues.|other:{count} references had no likely issues.",
  listHeading: "The list as a whole",
  entryLabel: "Reference [{label}]",
  confidence: { high: "Likely issue", medium: "Worth a look" },
  suggestionLabel: "Suggested:",
  copyFix: "Copy fix",
  copied: "Copied!",
  why: "Why: {source} →",
  sources: {
    guide: "IEEE Reference Guide",
    manual: "IEEE Editorial Style Manual",
  },
  ruleTitles: {
    unnumbered: "References are not numbered",
    "numbering-missing": "Some references have no number",
    "numbering-duplicate": "A reference number is used twice",
    "numbering-start": "The list does not start at [1]",
    "numbering-gap": "Numbers are missing from the sequence",
    "numbering-order": "Numbers are out of sequence",
    "duplicate-entry": "The same source may be listed twice",
    "alphabetical-order": "The list looks alphabetical",
    "bracket-numbers": "Reference numbers are not in square brackets",
    "author-inverted": "Author names are inverted",
    "author-initials-after": "Initials come after the surname",
    "author-suffix-comma": "Comma before Jr., Sr., or III",
    "author-ampersand": "“&” between authors",
    "author-and-missing": "No “and” before the last author",
    "author-serial-comma": "No comma before the final “and”",
    "author-two-comma": "Comma between two authors",
    "et-al-threshold": "More than six authors listed",
    "et-al-first-only": "Several names before et al.",
    "title-quotes-missing": "Article title not in quotation marks",
    "title-single-quotes": "Single quotation marks around the title",
    "title-punctuation": "Comma or period outside the closing quotation mark",
    "locator-style": "Volume and pages in Vancouver form",
    "volume-format": "Volume not written “vol.”",
    "issue-format": "Issue not written “no.”",
    "pages-format": "Pages not written “pp.” or “p.”",
    "month-format": "Month not in IEEE’s abbreviated form",
    "doi-format": "DOI not written “doi: 10.…”",
    "terminal-period": "Reference does not end with a period",
  } satisfies Record<IeeeRule, string>,
  ruleMessages: {
    unnumbered:
      "IEEE numbers every reference in square brackets — [1], [2], [3] — in the order the sources are first cited in your text. None of these references has a number.",
    "numbering-missing":
      "Every IEEE reference has its own number. These entries, counting from the top, have none: {entries}.",
    "numbering-duplicate":
      "[{number}] appears more than once. Each number belongs to exactly one reference.",
    "numbering-start":
      "The first number is [{first}]. A complete IEEE list starts at [1] — ignore this if you pasted only part of your list.",
    "numbering-gap":
      "These numbers are missing: {numbers}. If a reference was removed, renumber the list and the citations in your text.",
    "numbering-order":
      "[{number}] comes after [{previous}]. IEEE lists references in numerical order, and the numbers follow the order in which sources are first cited.",
    "duplicate-entry":
      "References [{first}] and [{second}] look like the same source (identical text or the same DOI). IEEE allows one reference per source: keep [{first}] and add a locator in the text where needed, such as [{first}, p. 5].",
    "alphabetical-order":
      "These references run A–Z by first author. IEEE orders references by first citation, not alphabetically — a list sorted A–Z usually means it was converted from an author–date style without renumbering.",
    "bracket-numbers":
      "IEEE sets reference numbers in square brackets, flush left: {example}, not 1. or (1).",
    "author-inverted":
      "IEEE puts the initials before the surname for every author — J. K. Author — rather than inverting names the way APA and Harvard do.",
    "author-initials-after":
      "IEEE writes initials, with periods, before the surname: J. K. Smith, not Smith JK (the Vancouver and AMA form).",
    "author-suffix-comma":
      "The IEEE Reference Guide says not to use commas around Jr., Sr., or III: Michael Smith Jr., not Michael Smith, Jr.",
    "author-ampersand":
      "IEEE joins the last two authors with “and”, never “&”.",
    "author-and-missing":
      "With three or more authors, IEEE puts “, and” before the last name: A. Lee, B. Chen, and C. Wu.",
    "author-serial-comma":
      "Every multi-author example in the IEEE Reference Guide has a comma before the final “and”: A. Lee, B. Chen, and C. Wu.",
    "author-two-comma":
      "Two authors are joined by “and” with no comma: M. Roe and N. Poe.",
    "et-al-threshold":
      "IEEE lists every author up to six. This reference names {count}, so give the first author followed by et al.",
    "et-al-first-only":
      "When IEEE shortens an author list, only the first author comes before et al. — not two or three names, as in Vancouver or AMA.",
    "title-quotes-missing":
      "This looks like an article or conference paper, but no title in quotation marks was found. IEEE puts article and paper titles in double quotation marks, with the comma inside: “Name of paper,”.",
    "title-single-quotes":
      "IEEE uses double quotation marks around article titles — “Name of paper,” — not single ones.",
    "title-punctuation":
      "IEEE puts commas and periods inside the closing quotation mark; only colons and semicolons go outside it.",
    "locator-style":
      "{found} is the Vancouver and AMA form. IEEE spells the parts out: {expected}.",
    "volume-format":
      "IEEE writes volume as lowercase “vol.” with a space before the number: {expected}, not {found}.",
    "issue-format":
      "IEEE writes the issue as lowercase “no.”: {expected}, not {found}.",
    "pages-format":
      "IEEE writes a page range as pp. 12–18, with an en dash, and a single page as p. 12. Found {found}; expected {expected}.",
    "month-format":
      "Write {expected} rather than {found} — the 2025 IEEE Reference Guide abbreviates months to three letters with a period (Jan., Feb., Sep.) and writes May in full. If your journal’s template uses another form, follow the template.",
    "doi-format":
      "IEEE writes a DOI as lowercase “doi:”, a space, and the DOI, and the reference still ends with a period: {expected}",
    "terminal-period":
      "Every IEEE reference ends with a period, including one that ends with a DOI. Only a reference that ends with a URL takes no final period.",
  } satisfies Record<IeeeRule, string>,
  checksHeading: "What this checker looks for",
  checks: [
    "Numbering: [1], [2], [3] in square brackets, with no gaps, repeats or numbers out of sequence.",
    "Authors: initials before surnames (J. K. Author), “and” before the last name with a comma when there are three or more, all names up to six, then the first author and et al.",
    "Titles of articles and papers in double quotation marks, with the comma inside: “Name of paper,”.",
    "Locators: vol., no., pp. or p., and months abbreviated the IEEE way — Jan., Feb., Sep.",
    "DOIs written doi: 10.xxxx, and a period at the end of every reference that does not end with a URL.",
    "The same source listed twice, and lists sorted A–Z instead of by first citation.",
  ],
  limits:
    "It cannot see italics, so it does not check that periodical and book titles are italic, and it does not check journal abbreviations against IEEE’s list. Every finding is a likely issue to review, not a verdict — your list is never rewritten for you.",
} as const;

type Strings = typeof CITATION_CHECKER_IEEE_STRINGS;

function reportLabels(
  report: IeeeReport,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): CheckerLabels {
  const entries = tPlural(strings.entriesCount, locale, report.totalEntries, {
    count: fmt(report.totalEntries),
  });
  const issues = tPlural(strings.issuesCount, locale, report.totalFindings, {
    count: fmt(report.totalFindings),
  });
  const clean = report.entries.filter(
    (entry) => entry.findings.length === 0
  ).length;
  return {
    results: strings.results,
    summary:
      report.totalFindings === 0
        ? t(strings.noIssues, { entries })
        : t(strings.summary, { issues, entries }),
    noIssues: t(strings.noIssues, { entries }),
    noIssuesBody: strings.noIssuesBody,
    listHeading: strings.listHeading,
    entryLabel: strings.entryLabel,
    confidence: strings.confidence,
    suggestionLabel: strings.suggestionLabel,
    copyFix: strings.copyFix,
    copied: strings.copied,
    why: strings.why,
    cleanNote:
      report.totalFindings > 0 && clean > 0
        ? tPlural(strings.cleanCount, locale, clean, { count: fmt(clean) })
        : "",
  };
}

export default function CitationCheckerIeeeWidget() {
  const strings = useToolUi(CITATION_CHECKER_IEEE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => (deferred.trim() ? checkIeeeReferences(deferred) : null),
    [deferred]
  );
  const sources = useMemo(
    () => ({
      guide: { url: IEEE_SOURCES.guide, label: strings.sources.guide },
      manual: { url: IEEE_SOURCES.manual, label: strings.sources.manual },
    }),
    [strings]
  );

  return (
    <>
      <TextAreaField
        hint={strings.inputHint}
        id="ieee-check-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {report ? (
        <CheckerReport
          fmt={fmt}
          labels={reportLabels(report, strings, locale, fmt)}
          messages={strings.ruleMessages}
          report={report}
          sources={sources}
          titles={strings.ruleTitles}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H3}>{strings.checksHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          {strings.checks.map((check) => (
            <li key={check}>{check}</li>
          ))}
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.limits}
        </p>
      </section>
    </>
  );
}
